import { Button, ButtonProps, CircularProgress, styled } from '@mui/material';
import React from 'react';

import { h4, h5 } from '../../theme';
import type { IconElement } from '../icons/IconElement';
import { iconSlot } from '../icons/iconSlot';

export type ButtonSize = 'small' | 'medium' | 'large';

export type BaseButtonProps = Omit<
    ButtonProps,
    'size' | 'href' | 'startIcon' | 'endIcon' | 'loading' | 'loadingIndicator' | 'loadingPosition'
> & {
    size?: ButtonSize;
    /** Use this, not MUI's `loading`: this theme draws MUI's spinner almost white. */
    isLoading?: boolean;
    /** Drawn at the button's 16px (small, medium) or 20px (large). */
    startIcon?: IconElement;
    /** Drawn at the button's 16px (small, medium) or 20px (large). */
    endIcon?: IconElement;
};

// To open another site: `onClick={() => window.open(url, '_blank', 'noopener')}`.
/** The base of all button variants; it never navigates: pass `to` to a variant such as `PrimaryButton` for a link. */
export const BaseButton = React.forwardRef<HTMLButtonElement, BaseButtonProps>(
    ({ size = 'large', isLoading = false, ...props }, ref) => {
        const startIcon = isLoading ? <CircularProgress size={'auto'} /> : props.startIcon;

        switch (size) {
            case 'small':
                return (
                    <SmallButton {...props} startIcon={startIcon} disabled={isLoading || props.disabled} ref={ref} />
                );
            case 'medium':
                return (
                    <MediumButton {...props} startIcon={startIcon} disabled={isLoading || props.disabled} ref={ref} />
                );
            case 'large':
                return (
                    <LargeButton {...props} startIcon={startIcon} disabled={isLoading || props.disabled} ref={ref} />
                );
        }
    },
);

/** Link mode of the button variants; while loading it drops the router's `href` and click, as for a disabled link. */
export const LinkButton = React.forwardRef<HTMLButtonElement, BaseButtonProps & { href?: string }>(
    ({ href, onClick, isLoading, ...props }, ref) => (
        <BaseButton {...props} {...(isLoading ? {} : { href, onClick })} isLoading={isLoading} ref={ref} />
    ),
);

// Sizes icons inside any element, such as MUI's `startIcon` span, but not an icon placed directly in the label.
const SmallButton = styled(Button)({
    ...h5,
    height: '24px',
    padding: '3px 6px',
    minWidth: 'unset',
    whiteSpace: 'nowrap',
    ...iconSlot(16, { reach: 'grandchild' }),
    // The loading spinner isn't an icon to the slot, so it gets the same size here.
    '& .MuiCircularProgress-svg': {
        width: '16px',
        height: '16px',
    },
    '& * > svg': {
        marginLeft: '-2px',
    },
    '& .MuiButton-startIcon ': {
        margin: '0px 4px 0px 0px',
    },
});

const MediumButton = styled(Button)({
    ...h5,
    height: '32px',
    padding: '8px 12px',
    whiteSpace: 'nowrap',
    ...iconSlot(16, { reach: 'grandchild' }),
    '& .MuiCircularProgress-svg': {
        width: '16px',
        height: '16px',
    },
    '& .MuiButton-startIcon ': {
        margin: '0px 4px 0px 0px',
    },
});

const LargeButton = styled(Button)({
    ...h4,
    height: '40px',
    padding: '10px 12px',
    whiteSpace: 'nowrap',
    ...iconSlot(20, { reach: 'grandchild' }),
    '& .MuiCircularProgress-svg': {
        width: '20px',
        height: '20px',
    },
    '& .MuiButton-startIcon ': {
        margin: '0px 8px 0px 0px',
    },
});
